/**
 * The sidebar is data, not markup.
 *
 * Every one of the 31 menu rows used to be a literal `<li>` in app.component.html
 * carrying its own `*ngIf="authService.hasPageAccess('...')"`. That made the menu
 * impossible to search, group or reorder without editing 200 lines of template,
 * and it re-ran the whole permission table on every change-detection cycle.
 */

/** RBAC key(s) from the `web.pages` registry. An array means "any of these". */
export type PageKey = string | string[]

export interface NavLeaf {
  /** Stable identity for favorites/recents — must survive label and route edits. */
  id: string
  label: string
  route: string
  icon?: string
  /**
   * Omitted for children that inherit their parent's gate. The almosafer and
   * requests submenus gate the whole section with a single key and leave their
   * children ungated; reproducing that faithfully is why this is optional.
   */
  pageKey?: PageKey
  /** Extra terms the command palette matches on, beyond `label`. */
  keywords?: string[]
}

export interface NavGroup {
  id: string
  label: string
  icon: string
  /**
   * Gate on the group itself, evaluated in addition to the children's own gates.
   * Kept separate rather than derived from the children because the original menu
   * did not always cover every child: the entertainment submenu was gated on
   * `entertainment || entertainment-alhokair` while holding a third child keyed
   * `entertainment-purchased-tickets`.
   */
  /**
   * Set when the group is also a destination: a tabbed hub page whose tabs are
   * exactly `children`. The sidebar then shows one link instead of a submenu,
   * while search and breadcrumbs still know about every page inside it.
   */
  route?: string
  pageKey?: PageKey
  /**
   * Recursive: a group may hold groups. Sections that are slated to become tabbed
   * hub pages (almosafer, requests, settings…) are modelled as nested groups until
   * the hub route exists, so the tree they present never has to be reshuffled.
   */
  children: NavItem[]
}

export type NavItem = NavLeaf | NavGroup

export function isNavGroup(item: NavItem): item is NavGroup {
  return Array.isArray((item as NavGroup).children)
}
